import { usePathname } from 'expo-router'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import type { View } from 'react-native'

import { useOnChange } from '@/src/general/hooks/useOnChange'

import { AccessibilitySkippedMenu } from '../ui/components/AccessibilitySkippedMenu'

type TAccessibilityContext = {
  initializeRef: (
    ref: View | null,
    type: 'main' | 'navigation' | 'menu',
  ) => MutableRefObject<View | null> | null | undefined
}

const AccessibilityContext = createContext<TAccessibilityContext>({
  initializeRef: () => null,
})

const INITIAL_REFS_STATE = {
  main: false,
  navigation: false,
  menu: false,
} as const

/**
 * AccessibilityContext Provider
 *
 * Provides a initializeRef function to initialize refs for main content, navigation and menu components in the page.
 * Depending on the ref initialization, the AccessibilitySkippedMenu component will render the accessibility menu buttons.
 */
export const AccessibilityProvider = ({ children }: PropsWithChildren): ReactNode => {
  const refToMainContent = useRef<View | null>(null)
  const refToNavigation = useRef<View | null>(null)
  const refToMenu = useRef<View | null>(null)
  const [refsState, setRefsState] = useState<Record<'main' | 'navigation' | 'menu', boolean>>(INITIAL_REFS_STATE)
  const pathname = usePathname()

  useOnChange(pathname, () => {
    setRefsState(INITIAL_REFS_STATE)
  })

  const initializeRef = useCallback(
    (ref: View | null, type: 'main' | 'navigation' | 'menu') => {
      if (ref === null) return null

      setRefsState((prevRefs) => ({ ...prevRefs, [type]: true }))

      if (type === 'main') {
        refToMainContent.current = ref
        return refToMainContent
      }
      if (type === 'navigation') {
        refToNavigation.current = ref
        return refToNavigation
      }
      if (type === 'menu') {
        refToMenu.current = ref
        return refToMenu
      }
    },
    [setRefsState, refToMainContent, refToNavigation, refToMenu],
  )

  const values = useMemo(
    () => ({
      initializeRef,
    }),
    [initializeRef],
  )

  return (
    <AccessibilityContext.Provider value={values}>
      <AccessibilitySkippedMenu
        refToMainContent={refToMainContent}
        refToNavigation={refToNavigation}
        refToMenu={refToMenu}
        refsState={refsState}
      />
      {children}
    </AccessibilityContext.Provider>
  )
}

export const useAccessibilityContext = () => useContext(AccessibilityContext)
